import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  SparklesIcon,
  PlayIcon,
  RocketLaunchIcon,
  CpuChipIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import {
  getFeatureMappings,
  bulkUpdateFeatureModels,
} from "../../utils/api/aiFeatureModel_apis";
import { AI_FEATURE_KEYS, AI_PROVIDERS } from "../../enums/AIModelEnums";
import { useSnackbar } from "../../utils/useSnackbar";
import AppSelect from "../Select/Select";
import SwitchComponent from "../FormComponent/SwitchComponent";
import ButtonComponent from "../customButton";

const SECTION_ORDER = [
  AI_FEATURE_KEYS.AUTOMATION_RECORDING,
  AI_FEATURE_KEYS.REPLAY_FLOW,
  AI_FEATURE_KEYS.TEST_LAUNCH,
  AI_FEATURE_KEYS.AI_GENERATOR,
  AI_FEATURE_KEYS.SECURITY_GUARDRAILS,
];

const SECTION_ICON_BY_KEY = {
  [AI_FEATURE_KEYS.AUTOMATION_RECORDING]: SparklesIcon,
  [AI_FEATURE_KEYS.REPLAY_FLOW]: PlayIcon,
  [AI_FEATURE_KEYS.TEST_LAUNCH]: RocketLaunchIcon,
  [AI_FEATURE_KEYS.AI_GENERATOR]: CpuChipIcon,
  [AI_FEATURE_KEYS.SECURITY_GUARDRAILS]: ShieldCheckIcon,
};

export default function SectionModelList({ providerSignature = "" }) {
  const { showSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingByFeature, setPendingByFeature] = useState({});
  const [applyingRecommended, setApplyingRecommended] = useState(false);
  const [confirmRecommendedOpen, setConfirmRecommendedOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFeatureMappings();
      const data = res?.data?.data || [];
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, providerSignature]);

  const ordered = useMemo(() => {
    const byKey = Object.fromEntries(rows.map((r) => [r.feature_key, r]));
    return SECTION_ORDER.map((k) => byKey[k]).filter(Boolean);
  }, [rows]);

  const compositeValue = useCallback(
    (m) => (m ? JSON.stringify({ provider: m.provider, model_key: m.model_key }) : ""),
    []
  );

  useEffect(() => {
    const nextPending = {};
    for (const feature of ordered) {
      nextPending[feature.feature_key] = compositeValue(feature.current_model || null);
    }
    setPendingByFeature(nextPending);
  }, [ordered, compositeValue]);

  const providerPriority = useCallback((providerKey) => {
    const provider = Object.values(AI_PROVIDERS).find((p) => p.key === providerKey);
    return provider?.priority ?? 99;
  }, []);

  const isCurrentRecommended = useCallback((feature) => {
    const current = feature?.current_model;
    if (!current?.provider || !current?.model_key) return false;
    return (feature?.available_models || []).some(
      (m) =>
        m.is_recommended &&
        m.provider === current.provider &&
        m.model_key === current.model_key
    );
  }, []);

  const allUsingRecommended = useMemo(() => {
    if (!ordered.length) return false;
    return ordered.every((feature) => isCurrentRecommended(feature));
  }, [ordered, isCurrentRecommended]);

  const getPriorityRecommendedModel = useCallback(
    (feature) => {
      const recommended = (feature?.available_models || [])
        .filter((m) => m.is_recommended)
        .sort((a, b) => {
          const byProvider = providerPriority(a.provider) - providerPriority(b.provider);
          if (byProvider !== 0) return byProvider;
          return (a.display_name || a.model_key || "").localeCompare(
            b.display_name || b.model_key || ""
          );
        });
      return recommended[0] || null;
    },
    [providerPriority]
  );

  const recommendedUpdatesCount = useMemo(() => {
    return ordered.reduce((count, feature) => {
      const recommended = getPriorityRecommendedModel(feature);
      if (!recommended) return count;
      const current = feature.current_model;
      const needsUpdate =
        current?.provider !== recommended.provider || current?.model_key !== recommended.model_key;
      return needsUpdate ? count + 1 : count;
    }, 0);
  }, [ordered, getPriorityRecommendedModel]);

  const applyRecommendedModels = useCallback(async () => {
    if (applyingRecommended) return;

    setApplyingRecommended(true);
    try {
      const updates = [];
      for (const feature of ordered) {
        const recommended = getPriorityRecommendedModel(feature);
        if (!recommended) continue;
        const current = feature.current_model;
        const alreadySet =
          current?.provider === recommended.provider &&
          current?.model_key === recommended.model_key;
        if (alreadySet) continue;
        updates.push({
          featureKey: feature.feature_key,
          provider: recommended.provider,
          modelKey: recommended.model_key,
        });
      }
      if (updates.length) await bulkUpdateFeatureModels(updates);
      await load();
      showSnackbar("Applied recommended models to all sections", "success");
    } catch (error) {
      showSnackbar(error?.message || "Failed to apply recommended models", "error");
    } finally {
      setApplyingRecommended(false);
    }
  }, [applyingRecommended, ordered, getPriorityRecommendedModel, load, showSnackbar]);

  const handleRecommendedSwitch = (event) => {
    const turnOn = event.target.checked;
    if (!turnOn || applyingRecommended || allUsingRecommended) return;
    setConfirmRecommendedOpen(true);
  };

  const handleConfirmRecommendedClose = () => {
    if (applyingRecommended) return;
    setConfirmRecommendedOpen(false);
  };

  const handleConfirmRecommendedApply = async () => {
    await applyRecommendedModels();
    setConfirmRecommendedOpen(false);
  };

  const hasUnsavedChanges = useMemo(() => {
    return ordered.some((feature) => {
      const current = compositeValue(feature.current_model || null);
      const pending = pendingByFeature[feature.feature_key] || "";
      return current !== pending;
    });
  }, [ordered, pendingByFeature, compositeValue]);

  const handleModelChange = useCallback((featureKey, nextValue) => {
    setPendingByFeature((prev) => ({
      ...prev,
      [featureKey]: nextValue,
    }));
  }, []);

  const handleSaveAll = useCallback(async () => {
    if (saving || !hasUnsavedChanges) return;
    setSaving(true);
    try {
      const updates = ordered
        .map((feature) => {
          const current = compositeValue(feature.current_model || null);
          const pending = pendingByFeature[feature.feature_key] || "";
          if (current === pending || !pending) return null;
          const parsed = JSON.parse(pending);
          return {
            featureKey: feature.feature_key,
            provider: parsed.provider,
            modelKey: parsed.model_key,
          };
        })
        .filter(Boolean);

      if (!updates.length) {
        setSaving(false);
        return;
      }

      await bulkUpdateFeatureModels(updates);
      await load();
      showSnackbar("Section model configuration updated", "success");
    } catch (error) {
      showSnackbar(error?.message || "Failed to update section model configuration", "error");
    } finally {
      setSaving(false);
    }
  }, [saving, hasUnsavedChanges, ordered, compositeValue, pendingByFeature, load, showSnackbar]);

  const handleCancelChanges = useCallback(() => {
    const resetPending = {};
    for (const feature of ordered) {
      resetPending[feature.feature_key] = compositeValue(feature.current_model || null);
    }
    setPendingByFeature(resetPending);
  }, [ordered, compositeValue]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (ordered.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No section configuration available.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        sx={{ mt: 1.25, mb: 0.5 }}
      >
        <Typography variant="h6" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
          Section Model Configuration
        </Typography>
        {/* Temporarily hidden as requested
        <Box sx={{ ml: "auto", flexShrink: 0 }}>
          <SwitchComponent
            label="Use Recommended Models"
            checked={allUsingRecommended}
            onChange={handleRecommendedSwitch}
            disabled={applyingRecommended || loading}
            size="small"
          />
        </Box>
        */}
      </Stack>
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "14px",
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(15, 23, 42, 0.06)",
        }}
      >
        {ordered.map((feature, index) => {
          const sortedModels = [...(feature.available_models || [])].sort((a, b) => {
            const rec = Number(!!b.is_recommended) - Number(!!a.is_recommended);
            if (rec !== 0) return rec;
            const providerSort = providerPriority(a.provider) - providerPriority(b.provider);
            if (providerSort !== 0) return providerSort;
            return (a.display_name || a.model_key || "").localeCompare(
              b.display_name || b.model_key || ""
            );
          });
          const recommended = sortedModels.filter((m) => m.is_recommended);
          const other = sortedModels.filter((m) => !m.is_recommended);
          const dropdownOptions = [...recommended, ...other].map((m) => ({
            value: compositeValue(m),
            label: m.display_name || m.model_key,
            isRecommended: Boolean(m.is_recommended),
            chipLabel: m.is_recommended ? "recommended" : "",
          }));

          return (
            <Box
              key={feature.feature_key}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "minmax(160px, 1fr) minmax(210px, 280px)" },
                gap: 0.75,
                alignItems: "center",
                px: 2,
                py: 1,
                borderBottom:
                  index === ordered.length - 1 ? "none" : "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                {(() => {
                  const SectionIcon = SECTION_ICON_BY_KEY[feature.feature_key];
                  return SectionIcon ? (
                    <SectionIcon style={{ width: 16, height: 16, color: "#64748B", flexShrink: 0 }} />
                  ) : null;
                })()}
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, lineHeight: 1.1 }}>
                  {feature.label}
                </Typography>
              </Box>
              <AppSelect
                label="Model"
                value={pendingByFeature[feature.feature_key] || ""}
                options={dropdownOptions}
                onChange={(e) => handleModelChange(feature.feature_key, e.target.value)}
                formWidth="100%"
                size="small"
                labelSx={{ fontSize: "11px", lineHeight: "12px" }}
                selectSx={{
                  "& .MuiSelect-select": {
                    fontSize: "14px",
                    py: 0.75,
                  },
                }}
                menuItemSx={{
                  fontSize: "14px",
                  minHeight: 34,
                  py: 0.5,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 280,
                    },
                  },
                }}
              />
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.75 }}>
        <ButtonComponent
          variant="primary"
          ctaText={saving ? "Saving..." : "Save"}
          onClick={handleSaveAll}
          disabled={!hasUnsavedChanges || saving}
          size="small"
          loading={saving}
          className="bg-primary white"
        />
        <ButtonComponent
          variant="secondary"
          ctaText="Cancel"
          onClick={handleCancelChanges}
          disabled={!hasUnsavedChanges || saving}
          size="small"
          className="bg-slate200 slate900"
        />
      </Box>
      <Dialog
        open={confirmRecommendedOpen}
        onClose={handleConfirmRecommendedClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Apply Recommended Models?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" component="div">
            This will update all section model configurations to their recommended models.
            {recommendedUpdatesCount > 0 ? (
              <>
                {" "}
                <Box
                  component="strong"
                  sx={{ fontWeight: 700, color: "text.primary" }}
                >
                  {recommendedUpdatesCount} section{recommendedUpdatesCount > 1 ? "s" : ""}
                </Box>{" "}
                will be changed.
              </>
            ) : (
              " No sections need changes right now."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmRecommendedClose} disabled={applyingRecommended}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRecommendedApply}
            disabled={applyingRecommended || recommendedUpdatesCount === 0}
            variant="contained"
            sx={{
              color: "#fff",
              bgcolor: "primary.main",
              "&:hover": {
                bgcolor: "primary.dark",
                color: "#fff",
              },
              "&.Mui-disabled": {
                bgcolor: "action.disabledBackground",
                color: "action.disabled",
              },
            }}
          >
            {applyingRecommended ? "Applying..." : "Apply"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
